import { useMemo } from 'react'

import type {
  ActivityRow,
  Holding,
  HoldingRow,
  PortfolioAccount,
  PortfolioAccountSummary,
  PortfolioDashboard,
  PortfolioPeriodOption,
  RealizedRow,
  RowContext,
} from './types'
import { useProjectSecurities } from './useProjectSecurities'

// One missing value makes the whole total a guess, so the total is unknown rather than short. With
// nothing to add up there is no total either, which keeps an activity-only period off $0.00.
export const sumOrNull = (values: (number | null)[]): number | null =>
  values.length === 0 || values.some((value) => value == null)
    ? null
    : values.reduce<number>((total, value) => total + (value ?? 0), 0)

type HoldingTotals = Pick<
  Holding,
  'marketValue' | 'costBasis' | 'unrealizedGainLoss'
>

// The money figures for one snapshot of holdings. The engine's own per-row unrealized gain wins
// over market value minus cost, since a statement can print the gain without printing a cost basis.
export const snapshotTotals = (holdings: HoldingTotals[]) => {
  const marketValue = holdings.reduce(
    (total, holding) => total + (holding.marketValue ?? 0),
    0,
  )
  const costBasis = sumOrNull(holdings.map((holding) => holding.costBasis))
  const unrealized =
    sumOrNull(holdings.map((holding) => holding.unrealizedGainLoss)) ??
    (costBasis == null ? null : marketValue - costBasis)

  return {
    marketValue,
    costBasis,
    unrealized,
    unrealizedPercent:
      costBasis != null && costBasis > 0 && unrealized != null
        ? (unrealized / costBasis) * 100
        : null,
  }
}

const summarize = (account: PortfolioAccount): PortfolioAccountSummary => {
  // Periods arrive oldest -> newest from the fold. The newest one can carry only activity, so the
  // current snapshot is the newest period that actually lists positions.
  const latest =
    [...account.periods]
      .reverse()
      .find((period) => period.holdings.length > 0) ??
    account.periods[account.periods.length - 1]
  const { marketValue, costBasis, unrealized, unrealizedPercent } =
    snapshotTotals(latest.holdings)

  return {
    id: account.id,
    accountNumber: account.accountNumber,
    institution: account.institution,
    accountType: account.accountType,
    validated: account.validated,
    failingSections: account.failingSections,
    latestPeriodKey: latest.period,
    latestPeriodLabel: latest.label,
    marketValue,
    costBasis,
    unrealized,
    unrealizedPercent,
    holdingsCount: latest.holdings.length,
    periodCount: account.periods.length,
  }
}

// Flatten one account's periods into rows tagged with their account and period, so tables can
// read across accounts and periods at once.
const flattenAccount = (
  account: PortfolioAccount,
): {
  holdings: HoldingRow[]
  activity: ActivityRow[]
  realized: RealizedRow[]
} => {
  const holdings: HoldingRow[] = []
  const activity: ActivityRow[] = []
  const realized: RealizedRow[] = []

  for (const period of account.periods) {
    const context: RowContext = {
      account: account.id,
      accountLabel: account.institution,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
      period: period.period,
      periodLabel: period.label,
    }

    for (const holding of period.holdings) {
      holdings.push({
        ...holding,
        ...context,
        gainLoss: snapshotTotals([holding]).unrealized,
      })
    }
    for (const entry of period.activity) {
      activity.push({ ...entry, ...context })
    }
    for (const gain of period.realizedGains) {
      realized.push({ ...gain, ...context })
    }
  }

  return { holdings, activity, realized }
}

// Distinct statement periods across every account, newest first. Keyed by period key so two
// accounts sharing a month collapse to one. A period with only activity still belongs in the list.
const distinctPeriods = (
  rows: { period: string; periodLabel: string }[],
): PortfolioPeriodOption[] => {
  const byKey = new Map<string, string>()
  for (const row of rows) {
    if (!byKey.has(row.period)) byKey.set(row.period, row.periodLabel)
  }
  return Array.from(byKey.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((first, second) => second.key.localeCompare(first.key))
}

// Fold the project's securities into the shape the Portfolio dashboard renders: per-account
// summaries, distinct periods, and flattened holding/activity/realized rows. Read-only.
export const usePortfolioDashboard = (): PortfolioDashboard => {
  const accounts = useProjectSecurities()

  return useMemo(() => {
    const summaries = accounts.map(summarize)
    const holdings: HoldingRow[] = []
    const activity: ActivityRow[] = []
    const realized: RealizedRow[] = []

    for (const account of accounts) {
      const flat = flattenAccount(account)
      holdings.push(...flat.holdings)
      activity.push(...flat.activity)
      realized.push(...flat.realized)
    }

    return {
      accounts: summaries,
      periods: distinctPeriods([...holdings, ...activity, ...realized]),
      holdings,
      activity,
      realized,
    }
  }, [accounts])
}
